// kfe-free-trial.js — the iOS free-trial screen (Brief 016).
//
// PORT TARGET: iOS `FreeTrial.storyboard` + `FreeTrialViewController.swift`
// (`KFEModules/FreeTrial/`). The storyboard is authored on the same 375x667
// retina4_7 canvas as the login xib and the forgot-password storyboard, so CSS
// px and iOS points are the same unit on a phone and every number below is
// lifted from it unchanged — the literal-port approach Brief 121 established
// for the login screen and Brief 200 reused.
//
// THE FLOW (iOS, mirrored — Rule #9):
//
//   /login, "Log In" tapped
//     -> the CRM refuses the login with `data.no_license: true`  (kfe-login.js)
//     -> this screen, with the username carried over in `?username=`
//     -> "Begin Free Trial" -> POST /app/activate-trial
//     -> signed in as a trial user, with full access  -> the pathway
//
// ---------------------------------------------------------------------------
// FIVE THINGS A READER WILL WANT TO CHECK, BECAUSE THEY LOOK LIKE MISTAKES
// ---------------------------------------------------------------------------
//
// 1. THE BUTTON SAYS "Begin Free Trial", NOT "Register free trial".
//    Brief 016 §"What iOS does" item 1 describes *"a 'Register free trial'
//    button (EN) / 注册免费体验 (ZH)"*. Those strings are real — they are in
//    `Language.strings:222` — but they belong to a DIFFERENT, earlier screen
//    (the one whose prompt is *"Please input your username / registered email
//    to register for a Free Trial."*, `Language.strings:223`), which is not in
//    the shipped storyboard. The scene the login screen actually presents sets
//    its button title in `setupStyle()` to `tr._t("Begin Free Trial")`
//    (FreeTrialViewController.swift:38) = 开始免费体验 (`Language.strings:230`).
//    Rule #9 makes the phone canon, so that is the label here. Recorded in
//    docs/016-handover.md rather than resolved quietly — the same shape as
//    Brief 200's backdrop finding.
//
// 2. THIS SCREEN *DOES* SIT ON THE ORANGE LOGIN ARTWORK.
//    Which looks inconsistent right after Brief 200 found the forgot-password
//    screens are flat `#1A1A1A`. Both are correct: `ForgotPassword.storyboard`
//    has no image view at all, while this scene has a `LoginBackground`
//    `imageView` pinned to the root view's leading and trailing edges
//    (constraints `05j-8X-fDH` / `F3h-BS-KoL`) with `scaleAspectFill`. iOS's
//    own inconsistency IS the canon (CLAUDE.md Rule #9, "do NOT collapse,
//    unify, normalise"), so the two screens differ here exactly as they do on
//    the phone.
//
// 3. THERE IS NO BACK BUTTON, AND NO EMPTY-PASSWORD GUARD.
//    Both are absences in the source, not omissions here. The scene has no
//    back control and no dismiss action other than a successful
//    `loginClicked:`, because iOS presents it modally over the login screen
//    and only tears it down on success. And `loginClicked:` runs
//    `activateTrial()` unconditionally — there is no `toggleConfirm`-style
//    enabling and no length check, so an empty password is sent and the
//    server's refusal is what the learner sees. Mirrored. (Note this differs
//    from `kfe-login`'s own submit, which DOES return early on an empty
//    field — that is Brief 188's web behaviour on a different screen, not a
//    rule to spread.) The browser's own Back button is the web's way off this
//    screen; nothing had to be invented for it.
//
// 4. THE BUTTON'S ORANGE IS #F87D18, NOT THE LOGIN SCREEN'S #FF931E.
//    `rxM-69-yC6`'s `backgroundColor` is `red=0.97254901959999995
//    green=0.49019607840000001 blue=0.094117647060000004` = rgb(248,125,24),
//    and `setupStyle()` adds a **0.5pt** white border with cornerRadius **5**.
//    The login xib's Log In button is #FF931E with a 1pt border and radius 4.
//    Three small differences, all deliberate: they are what the two files say.
//    (#F87D18 is also the forgot-password Confirm button's fill, so this
//    screen agrees with that one rather than with the login screen.)
//
// 5. A DIRECT VISIT WITH NO `?username=` GOES TO /login.
//    iOS can only reach this scene from a login failure and always sets
//    `vc.username` (`showFreeTrial`, KFELoginViewController.m:281-285), so
//    there is no iOS behaviour for "no username" to mirror. Brief 200 solved
//    the equivalent gap on /forgot-password by adding a web-only username
//    box; that is wrong here, because a username box on this screen would be a
//    trial-SIGNUP entry point the phone does not have — exactly the "register
//    free trial" screen note 1 explains is not in the shipped app. Sending the
//    visitor to the login screen invents nothing and dead-ends nobody.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import * as router from "../router.js";
import { crmStartTrial, firstFieldError } from "../data/crm-client.js";
import { setSession, setLastUsername } from "../data/auth-store.js";
import { setTrialContextFromLogin } from "../data/trial-store.js";
import { ASSET_BASE } from "../ui/kfe-splash.js";

export class KFEFreeTrial extends KFEComponent {
  static styles = `
    /* GEOMETRY IS THE STORYBOARD'S. FreeTrial.storyboard, 375x667 points:

         LoginBackground   pinned leading/trailing, scaleAspectFill
         the stack         50pt side insets -> 275 wide, centred on centreY,
                           40pt field + 15pt gap + 40pt button = 95 tall
         info label        40pt side insets -> 295 wide, 50pt above the field
         "Congratulations" centreX, 20pt above the info label
         note label        50pt side insets -> 275 wide, 30pt off the bottom

       The vertical chain hangs UPWARD from the stack: the storyboard gives
       "Congratulations" no top constraint at all (only centreX), and derives
       its position from infoLabel.top - 20, which comes from
       passwordText.top - 50, which comes from the stack being centred
       (constraint Jt3-rK-Vst). So the two labels are laid out as a column
       anchored by its BOTTOM edge — which also means they grow upwards as the
       text wraps, exactly as the constraints make them. */
    :host {
      position: fixed; inset: 0;
      display: block;
      color: #fff;
      font-family: var(--kfe-font);
      /* iOS draws this scene in IdealSans (Medium 16, Semibold 17/32), which
         the web build does not licence or vendor. Every size and weight below
         is the storyboard's; the face is the app's own stack. The same single
         unavoidable divergence Briefs 121 and 200 both carry. */
    }

    /* LoginBackground — the SAME artwork as the splash and the login screen,
       so a learner refused for want of a licence stays on one unbroken
       backdrop. See header note 2 for why this screen has it and
       /forgot-password does not. */
    .backdrop {
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      object-fit: cover; object-position: center;
      pointer-events: none;
    }

    /* iOS is portrait-phone only, so the storyboard has no opinion about a
       1400px window. Brief 121 settled this: keep Brief 198's app column and
       lay the storyboard out inside it. At or below 480px — every phone — this
       container is the full viewport and the geometry is byte-identical. */
    .screen {
      position: absolute; inset: 0;
      max-width: var(--kfe-app-max-width, 480px);
      margin-inline: auto;
    }

    /* The container view (cFd-tE-FSS): 50pt insets, centred on centreY, 95
       tall. top = 50% - 47.5px puts its centre exactly on the centre line,
       which on the storyboard's canvas is its own frame y=286. */
    .stack {
      position: absolute;
      left: 50px; right: 50px;
      top: calc(50% - 47.5px);
    }

    /* The password field (pYG-85-UfT): black, white centred Semibold 17, 40
       tall. borderStyle=roundedRect draws nothing once backgroundColor is
       opaque black — the same reason the login xib's fields render square
       (Brief 121, docs/121-proof/ios-login.jpg). */
    .stack input {
      display: block; box-sizing: border-box;
      width: 100%; height: 40px; margin: 0 0 15px;
      border: 0; border-radius: 0;
      background: #000; color: #fff;
      font-family: inherit; font-size: 17px; font-weight: 600;
      text-align: center;
    }
    /* attributedPlaceholder, UIColor(red:203/255, green:203/255, blue:203/255)
       — FreeTrialViewController.swift:36. NOT the login screen's
       rgba(255,255,255,.8); the two files differ and both are kept. */
    .stack input::placeholder { color: rgb(203, 203, 203); }
    .stack input:focus { outline: 2px solid #fff; outline-offset: -2px; }
    .stack input[disabled] { opacity: 0.6; }

    /* The button (rxM-69-yC6): width tied to the field, 40 tall, fill
       rgb(248,125,24), and setupStyle()'s cornerRadius 5 + 0.5pt white
       border. See header note 4 on why these three numbers differ from the
       login screen's. */
    button.primary {
      display: block; box-sizing: border-box;
      width: 100%; height: 40px; margin: 0;
      border: 0.5px solid #fff; border-radius: 5px;
      background: #F87D18; color: #fff;
      font-family: inherit; font-size: 17px; font-weight: 600;
      cursor: pointer;
    }
    button.primary[disabled] { opacity: 0.5; cursor: not-allowed; }

    /* The two labels above the stack, as one bottom-anchored column (see the
       .stack comment). The info label's bottom edge sits 50pt above the
       field's top edge: 50% + 47.5 (half the stack) + 50 = 50% + 97.5px
       measured from the bottom. */
    .labels {
      position: absolute;
      left: 0; right: 0;
      bottom: calc(50% + 97.5px);
      display: flex; flex-direction: column; align-items: center;
      gap: 20px;
    }
    /* loginLabel (TB0-U0-bEs): IdealSans-Semibold 32, white, centreX, single
       line (no numberOfLines, tailTruncation). */
    .labels h1 {
      margin: 0;
      font-size: 32px; font-weight: 600; line-height: 32px;
      white-space: nowrap;
    }
    /* infoLabel (V0B-ep-55H): IdealSans-Medium 16, white, centred,
       numberOfLines 0, 40pt side insets -> 295 at the canvas width.

       line-height is NOT pinned to the storyboard's 32pt frame height. Those
       frames are Interface Builder's design-time measurements, not runtime
       truth: at runtime the label is intrinsically sized from its constraints
       and the font's own leading, and IdealSans-Medium 16 does not lay two
       wrapped lines into 32pt. Pinning 16px here would cramp the substitute
       face for no canon reason. The INVARIANTS the storyboard states — the
       40pt insets, the 20pt gap above and the 50pt gap below — are what the
       parity tests assert. */
    .labels p {
      margin: 0;
      width: calc(100% - 80px);
      text-align: center;
      font-size: 16px; font-weight: 500; line-height: 1.25;
    }

    /* noteLabel (kUi-6T-45P): IdealSans-Medium 16, white, centred,
       numberOfLines 0, 50pt insets, and 30pt off the bottom — the
       bottomLayoutGuide constraint aMM-tv-xE0. */
    .note {
      position: absolute;
      left: 50px; right: 50px; bottom: 30px;
      margin: 0;
      text-align: center;
      font-size: 16px; font-weight: 500; line-height: 1.25;
    }

    /* iOS raises the activate-trial failure in a modal
       (KFEAlertManager.shared.basic(title: message),
       FreeTrialViewController.swift:75). Brief 188 chose in-page errors for
       the pre-auth surfaces and Briefs 121 and 200 both kept that, so the
       alert's WORDS appear, in the page, directly under the button. Mirroring
       the modal is a decision someone should make for /login,
       /forgot-password and this screen at once — see docs/016-handover.md. */
    .error {
      margin: 15px 0 0; padding: 0.5rem 0.75rem;
      background: rgba(0, 0, 0, 0.85);
      font-size: 0.85rem; line-height: 1.35;
      color: #fff;
    }
  `;

  /**
   * The username the login screen was refused with, handed over in the URL
   * exactly as Brief 200 hands one to /forgot-password. iOS hands it directly
   * (`vc.username = self.loginManager.userName`); the web has a URL between
   * the two screens, and the query string is the slot that survives a reload.
   *
   * There is no second value to carry — unlike /forgot-password's `has_phone`,
   * which is the server's answer about the account and therefore had to travel
   * out of band — so there is no sessionStorage handoff here at all.
   * @type {string}
   */
  _username = new URLSearchParams(location.search).get("username")?.trim() ?? "";
  /** @type {string} */ _password = "";
  /** @type {boolean} */ _submitting = false;
  /** @type {string} */ _error = "";

  onConnected() {
    // Header note 5: nothing to activate a trial for. iOS cannot reach this
    // scene without a username, so there is no behaviour to mirror and a
    // username box here would invent a trial-signup surface the phone does
    // not have. replaceState rather than push, so the browser's Back button
    // does not bounce the learner straight back into the dead state.
    if (!this._username) {
      router.navigate("/login", { replace: true });
      return;
    }

    this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
      detail: { title: t("trial.congratulations") },
      bubbles: true,
      composed: true,
    }));

    const onI18n = () => this._scheduleRender();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));
  }

  /** @param {Event} e */
  _onPasswordInput = (e) => {
    this._password = /** @type {HTMLInputElement} */ (e.target).value;
  };

  /**
   * `loginClicked:` → `activateTrial()` (FreeTrialViewController.swift:50-78),
   * mirrored:
   *
   *   1. show the indicator, dismiss the keyboard, and call activate-trial
   *      with the carried-over username and whatever is in the password box —
   *      with NO length check (header note 3);
   *   2. on success, hand the payload through the same path a normal login
   *      takes, because that is literally what iOS does: it builds a
   *      `KFELoginManager`, calls `analysisWithisSuccess(true, …)` — which is
   *      where `KFE7DaysTrialMgr.setIs7DaysTrialWithServerMessage` and
   *      `appDidActivate` run (KFELoginManager.m:188-192) — and then calls the
   *      login screen's own `finishBlock`. So: store the session, remember the
   *      username, record the trial context, announce the sign-in, navigate;
   *   3. on failure, show the server's message and stay put, retryable.
   */
  _onSubmit = async (/** @type {Event} */ e) => {
    e.preventDefault();
    if (this._submitting) return;

    this._submitting = true;
    this._error = "";
    this._scheduleRender();

    const result = await crmStartTrial({ login: this._username, password: this._password });
    if (!this.isConnected) return;
    this._submitting = false;

    if (result.success) {
      setSession(result.data);
      setLastUsername(this._username);
      // The trial schedule has to be recorded from THIS response, not from a
      // later login: `is_trial_user` / `app_deactivate_date` arrive here, and
      // the welcome message is due the moment the learner lands. `result.raw`
      // is the server's `data` block verbatim — the trial fields keep iOS's
      // own snake_case names and live in their own store, so they are
      // deliberately not part of the normalised session (crm-client's
      // CrmResult docblock says why).
      setTrialContextFromLogin(result.raw);
      document.dispatchEvent(new Event("kfe:auth-changed"));
      router.navigate("/");
      return;
    }

    // `isFailure(activateTrial.message)` → `KFEAlertManager.shared.basic`
    // (FreeTrialViewController.swift:75).
    //
    // THE ENVELOPE'S `message` COMES FIRST HERE, which is the opposite of the
    // order Brief 200's screens use, and it is deliberate: iOS reads
    // `ActivateTrialResponse.message` for THIS call, where for
    // `check-username` it reads `json.errors![0].message[0]`
    // (KFEApiManager.swift:166). Two endpoints, two slots, mirrored
    // separately. The per-field sentence is kept as the fallback for a 400
    // whose envelope is the useless generic "Invalid input." — on the
    // recorded staging refusal both slots hold the same string, so the order
    // makes no difference there and only shows up on some future shape.
    this._error = result.error?.message || firstFieldError(result.error) || t("forgot.errorUnreachable");
    this._scheduleRender();
  };

  render() {
    return html`
      <img class="backdrop" alt=""
           src="${ASSET_BASE}/splash-bg@2x.png"
           srcset="${ASSET_BASE}/splash-bg@2x.png 2x, ${ASSET_BASE}/splash-bg@3x.png 3x" />
      <div class="screen">
        <div class="labels">
          <h1>${t("trial.congratulations")}</h1>
          <p>${t("trial.rejoinPrompt")}</p>
        </div>
        <form class="stack" on:submit=${this._onSubmit}>
          <input id="trial-password" name="password" type="password" autocomplete="current-password"
                 placeholder="${t("login.password")}" aria-label="${t("login.password")}"
                 .value=${this._password} .disabled=${this._submitting}
                 on:input=${this._onPasswordInput} />
          <button class="primary" id="trial-begin" type="submit" .disabled=${this._submitting}>
            ${this._submitting ? t("forgot.loading") : t("trial.begin")}
          </button>
          ${this._error ? html`<p class="error" role="alert">${this._error}</p>` : ""}
        </form>
        <p class="note">${t("trial.registeredMembersOnly")}</p>
      </div>
    `;
  }
}
